import { capitalize } from 'es-toolkit';
import type { ProductData, SuccessfulSessionStatusData } from '../../common';
import {
  fromStripeAmount,
  toStripeDisplayAmount
} from './convert-payment-amount';

export function processSuccessfulPaymentInfo({
  sessionStatusData,
  productData,
  locale,
  translatedSuccessStatus = 'Payment Received'
}: {
  locale: Intl.LocalesArgument;
  translatedSuccessStatus?: string;
  sessionStatusData?: SuccessfulSessionStatusData;
  productData?: ProductData;
}) {
  if (!sessionStatusData || !productData) {
    return;
  }

  const { amount, currency } = productData;
  const {
    chargeCreatedAt,
    paymentId,
    amount: paidAmount,
    paymentMethodType
  } = sessionStatusData;

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'short'
  });

  const numberFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0
  });

  const displayPrice = numberFormatter.format(
    toStripeDisplayAmount({ amount, currency })
  );
  const displayPaidAmount = numberFormatter.format(
    fromStripeAmount({ amount: paidAmount, currency })
  );
  const rawDate = new Date(chargeCreatedAt * 1000);
  const displayDate = dateFormatter.format(rawDate);
  const displayPaymentMethodType = paymentMethodType
    ? capitalize(paymentMethodType.replaceAll('_', ' '))
    : null;

  const refundUrl = paymentId
    ? `https://dashboard.stripe.com/payments/${paymentId}`
    : null;

  return {
    ...sessionStatusData,
    displayStatus: translatedSuccessStatus,
    displayPrice,
    displayPaidAmount,
    displayPaymentMethodType,
    rawDate,
    displayDate,
    refundUrl
  };
}

export type ProcessedPaymentInfo = ReturnType<
  typeof processSuccessfulPaymentInfo
>;

export function getSubmitPaymentData(
  processedPaymentInfo: ProcessedPaymentInfo
) {
  if (!processedPaymentInfo) {
    return;
  }

  const {
    rawDate,
    displayStatus,
    displayPaymentMethodType,
    displayPrice,
    displayPaidAmount,
    cardNumber,
    chargeId,
    invoicePaymentPageUrl,
    receiptUrl,
    refundUrl
  } = processedPaymentInfo;

  return {
    status: displayStatus,
    price: displayPrice,
    amount: displayPaidAmount,
    payment_method: displayPaymentMethodType,
    card_number: cardNumber,
    transaction_id: chargeId,
    date: rawDate.toISOString(),
    receipt_url: receiptUrl,
    invoice_url: invoicePaymentPageUrl,
    refund_url: refundUrl
  };
}
